// meetFlyState.js — the PURE reads of a call's Fly state (functions/meetFly.js
// writes it; workspace docs/FLY_MODE.md §3.3). Its own small file because the
// shell-root dock (components/CallWidget.js) reads the theater's state and
// must stay eager without pulling meetFly.js in (the ControlBarIconButton
// split's reason).
//
// root.meetFly (workspace-scoped, declared in workspace state-app.js):
//   { v: 2, roomId, mode, open, focus, hidden, pages }
//   mode   null, or { on, by: { identity, name }, at } — the room's switch as
//          last set (OFF keeps its time, so an older ON never wins); on: the
//          agent hears its name in anyone's speech
//   open   THIS person's theater shows (the main layer over the call)
//   focus  the key of the page the theater shows
//   hidden keys of pages this person hid (another's page; theirs stops)
//   pages  the call's pages, oldest first (≤ MEET_FLY_PAGES_MAX):
//          { key, roomId, agent: { key, name }, by: { identity, name },
//            mine, prompt, at, page }
//   confirm  null, or { id, prompt, agent: { key, name }, heardAt, at } —
//          THIS person's spoken ask waiting for their yes (meetFly.js
//          meetFlyConfirm); never on the wire
import { stripWorkspacePrefix } from '@symbo-ls/workspace-shared/functions/workspacePrefix.js'

export const MEET_FLY_PAGES_MAX = 12

// The Fly state of the call on screen, or null (another room's is stale).
export const meetFlyCallOf = (root) => {
  const r = root || {}
  const c = r.meetFly
  if (!c || typeof c !== 'object' || c.v !== 2) return null
  if (c.roomId && r.activeRoomId && c.roomId !== r.activeRoomId) return null
  return c
}

// The pages this person sees (their hidden ones left out), oldest first.
export const meetFlyVisiblePages = (call) => {
  if (!call || !Array.isArray(call.pages)) return []
  const hidden = new Set(Array.isArray(call.hidden) ? call.hidden : [])
  return call.pages.filter((p) => p && p.key && !hidden.has(p.key))
}

// The page the theater shows: the focused one, else the newest visible one.
export const meetFlyFocusedPage = (call) => {
  const pages = meetFlyVisiblePages(call)
  return pages.find((p) => p.key === (call && call.focus)) || pages[pages.length - 1] || null
}

// The person is on the call view itself (the /meet route, in a call).
export const meetFlyOnCallView = (root) => {
  const r = root || {}
  const raw =
    r.currentPath ||
    (typeof window !== 'undefined' && window.location ? window.location.pathname : '/')
  return stripWorkspacePrefix(raw) === '/meet' && r.meetView === 'call'
}

// THE THEATER shows: a live call, on its own view, with this person's theater
// open on a page they can see. The main layer lies over the call then (the
// call stays mounted underneath — its audio keeps playing) and the dock
// stands in for the call.
export const meetFlyTheaterOpen = (root) => {
  const r = root || {}
  if (!r.livekitConnected) return false
  const call = meetFlyCallOf(r)
  if (!call || call.open !== true || !meetFlyFocusedPage(call)) return false
  return meetFlyOnCallView(r)
}

// Fly is on in this call (anyone's toggle; the person who turned it on owns it).
export const meetFlyModeOn = (root) => {
  const call = meetFlyCallOf(root)
  return !!(call && call.mode && call.mode.on)
}

// The spoken ask waiting for this person's yes, while the call is live and
// Fly is on — the confirm card's read (components/MeetFlyConfirm.js), or null.
export const meetFlyConfirmOf = (root) => {
  const r = root || {}
  if (!r.livekitConnected || !meetFlyModeOn(r)) return null
  const c = meetFlyCallOf(r).confirm
  return c && typeof c === 'object' && c.id && c.prompt ? c : null
}
